import { AssigneeSelectComponent, AssigneeChange } from '../../company/assignee-select/assignee-select';
import { CompanyContextService } from '../../../services/company-context';
import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterModule, NavigationEnd } from '@angular/router';
import { ViewRequestBaseComponent } from '../../shared/view-request-base/view-request-base.component';
import { RequestBidsSectionComponent } from '../../shared/request-bids-section/request-bids-section.component';
import { ChatThreadsComponent } from '../../shared/chat-threads/chat-threads.component';
import { CargoRequestService, CargoRequest } from '../../../services/api/cargo-request';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { FileService, FileDto } from '../../../services/api/file';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ConfirmDialogService } from '../../../services/confirm-dialog';
import { DocNumberPipe } from '../../../services/request-number';

@Component({
  selector: 'app-view-request',
  standalone: true,
  imports: [CommonModule, RouterModule, AssigneeSelectComponent, ViewRequestBaseComponent, RequestBidsSectionComponent, DatePipe, ChatThreadsComponent, DocNumberPipe],
  templateUrl: './view-request.html',
  styleUrl: './view-request.scss'
})
export class ViewRequestComponent implements OnInit, OnDestroy {
  /** Подтверждение на странице вместо системного confirm() — его блокируют встроенные браузеры. */
  private confirmDialog = inject(ConfirmDialogService);

  /** Встроенный режим (вкладка «Перевозка» на странице обработки расчёта) — id заявки
   *  приходит напрямую, а не из параметров маршрута. */
  @Input() embeddedRequestId?: number;

  requestId: number | null = null;
  /** Из query-параметра ?forwarderId= — открыть сразу тред конкретного экспедитора и
   *  проскроллить к чату (переход по клику на push-уведомление о новом сообщении). */
  chatForwarderId: number | null = null;
  request: CargoRequest | null = null;
  hasBids = false;
  loadingRequest = false;
  deletingRequest = false;
  
  // Инвойс для таможенных платежей
  invoiceFile: FileDto | null = null;
  loadingInvoice = false;

  // Translations
  titleText = 'Просмотр запроса';
  backText = 'Назад к списку';
  chatButtonText = 'Чат';
  editRequestText = 'Редактировать';
  editDisabledText = 'Редактирование недоступно, так как есть ставки';
  deleteRequestText = 'Удалить';
  deletingRequestText = 'Удаление...';
  deleteRequestConfirmText = 'Вы уверены, что хотите удалить этот запрос?';
  
  // Товары для расчета таможенных платежей
  customsProductsSectionTitleText = 'Товары для расчета таможенных платежей';
  calculationCompletedText = 'Расчет завершен';
  calculationInProgressText = 'Брокер работает над расчетом';
  calculationPendingText = 'Ожидает расчета брокером';
  editCustomsProductsText = 'Редактировать товары';
  productNameText = 'Наименование';
  productTnvedText = 'Код ТНВЭД';
  productCostText = 'Стоимость товара';
  customsFeesText = 'Таможенные сборы';
  customsDutyText = 'Таможенная пошлина';
  vatText = 'НДС';
  totalPaymentsText = 'Общие платежи';
  totalAllPaymentsText = 'Итого';
  noCustomsProductsText = 'Товары не добавлены';
  invoiceText = 'Инвойс';
  downloadInvoiceText = 'Скачать инвойс';
  noInvoiceText = 'Инвойс не загружен';
  codeNotFoundText = 'Код не найден';
  completedWithAiText = 'Выполнено с помощью AI';
  loadingText = 'Загрузка...';
  inDictionaryBadgeText = '✓ В справочнике';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private cargoRequestService: CargoRequestService,
    private languageService: LanguageService,
    private authService: AuthService,
    private fileService: FileService,
    public companyContext: CompanyContextService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.companyContext.ensureLoaded();
    if (this.embeddedRequestId) {
      this.requestId = this.embeddedRequestId;
      this.updateTranslations();
      this.loadRequest(this.embeddedRequestId);
      return;
    }

    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('ViewRequest: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        console.warn('ViewRequest: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      if (user?.role?.code !== 'ROLE_CARGO_OWNER') {
        console.warn('ViewRequest: User is not cargo owner, redirecting to home');
        this.router.navigate(['/']);
        return;
      }

      const forwarderIdParam = this.route.snapshot.queryParamMap.get('forwarderId');
      if (forwarderIdParam) {
        this.chatForwarderId = +forwarderIdParam;
      }

      // Отслеживаем изменения параметров маршрута для перезагрузки данных
      this.subscriptions.add(
        this.route.paramMap.subscribe(params => {
          const id = params.get('id');
          if (id) {
            const requestId = +id;
            // Перезагружаем данные только если ID изменился или это первая загрузка
            if (this.requestId !== requestId || !this.request) {
              this.requestId = requestId;
              this.loadRequest(requestId);
            }
          }
        })
      );

      this.updateTranslations();

      this.subscriptions.add(
        this.languageService.getTranslations().subscribe(() => {
          this.updateTranslations();
        })
      );

      // Перезагружаем данные при возврате на страницу (например, после редактирования)
      this.subscriptions.add(
        this.router.events.pipe(
          filter(event => event instanceof NavigationEnd)
        ).subscribe((event: any) => {
          const id = this.route.snapshot.paramMap.get('id');
          if (id && event.url === `/cargo-owner/view-request/${id}` && this.requestId) {
            // Перезагружаем данные при возврате на эту страницу
            this.loadRequest(this.requestId);
          }
        })
      );
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('cargoOwner.viewRequest.title') || 'Просмотр запроса';
    this.backText = this.languageService.translate('cargoOwner.viewRequest.back') || 'Назад к списку';
    this.chatButtonText = this.languageService.translate('cargoOwner.viewRequest.chatButton') || 'Чат';
    this.editRequestText = this.languageService.translate('cargoOwner.viewRequest.editRequest') || 'Редактировать';
    this.editDisabledText = this.languageService.translate('cargoOwner.viewRequest.editDisabled') || 'Редактирование недоступно, так как есть ставки';
    this.deleteRequestText = this.languageService.translate('cargoOwner.dashboard.deleteRequest') || 'Удалить';
    this.deletingRequestText = this.languageService.translate('cargoOwner.dashboard.deletingRequest') || 'Удаление...';
    this.deleteRequestConfirmText = this.languageService.translate('cargoOwner.dashboard.deleteRequestConfirm') || 'Вы уверены, что хотите удалить этот запрос?';
    
    // Товары для расчета таможенных платежей
    this.customsProductsSectionTitleText = this.languageService.translate('cargoOwner.viewRequest.customsProductsSectionTitle') || 'Товары для расчета таможенных платежей';
    this.calculationCompletedText = this.languageService.translate('cargoOwner.viewRequest.calculationCompleted') || 'Расчет завершен';
    this.calculationInProgressText = this.languageService.translate('cargoOwner.viewRequest.calculationInProgress') || 'Брокер работает над расчетом';
    this.calculationPendingText = this.languageService.translate('cargoOwner.viewRequest.calculationPending') || 'Ожидает расчета брокером';
    this.editCustomsProductsText = this.languageService.translate('cargoOwner.viewRequest.editCustomsProducts') || 'Редактировать товары';
    this.productNameText = this.languageService.translate('cargoOwner.viewRequest.productName') || 'Наименование';
    this.productTnvedText = this.languageService.translate('cargoOwner.viewRequest.productTnved') || 'Код ТНВЭД';
    this.productCostText = this.languageService.translate('cargoOwner.viewRequest.productCost') || 'Стоимость товара';
    this.customsFeesText = this.languageService.translate('cargoOwner.viewRequest.customsFees') || 'Таможенные сборы';
    this.customsDutyText = this.languageService.translate('cargoOwner.viewRequest.customsDuty') || 'Таможенная пошлина';
    this.vatText = this.languageService.translate('cargoOwner.viewRequest.vat') || 'НДС';
    this.totalPaymentsText = this.languageService.translate('cargoOwner.viewRequest.totalPayments') || 'Общие платежи';
    this.totalAllPaymentsText = this.languageService.translate('cargoOwner.viewRequest.totalAllPayments') || 'Итого';
    this.noCustomsProductsText = this.languageService.translate('cargoOwner.viewRequest.noCustomsProducts') || 'Товары не добавлены';
    this.invoiceText = this.languageService.translate('cargoOwner.viewRequest.invoice') || 'Инвойс';
    this.downloadInvoiceText = this.languageService.translate('cargoOwner.viewRequest.downloadInvoice') || 'Скачать инвойс';
    this.noInvoiceText = this.languageService.translate('cargoOwner.viewRequest.noInvoice') || 'Инвойс не загружен';
    this.codeNotFoundText = this.languageService.translate('public.viewCalculation.codeNotFound') || 'Код не найден';
    this.completedWithAiText = this.languageService.translate('public.viewCalculation.completedWithAi') || this.completedWithAiText;
    this.loadingText = this.languageService.translate('common.loading') || this.loadingText;
    this.inDictionaryBadgeText = this.languageService.translate('cargoOwner.viewRequest.inDictionaryBadge') || this.inDictionaryBadgeText;

    this.cdr.markForCheck();
  }

  private loadRequest(id: number): void {
    this.loadingRequest = true;
    this.subscriptions.add(
      this.cargoRequestService.getCargoRequest(id).subscribe({
        next: (request) => {
          this.request = request;
          this.loadingRequest = false;

          // Загружаем инвойс, если есть товары для расчета таможенных платежей
          if (request.calculateCustomsPayments && request.id) {
            this.loadInvoice(request.id);
          }

          this.cdr.markForCheck();

          if (this.chatForwarderId) {
            // Тред переключает <app-chat-threads> после своей загрузки асинхронно —
            // ждём следующий тик рендера перед скроллом к уже видимому блоку чата.
            setTimeout(() => this.scrollToChat(), 0);
          }
        },
        error: (err) => {
          console.error('ViewRequest: Error loading request', err);
          this.loadingRequest = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  private loadInvoice(requestId: number): void {
    this.loadingInvoice = true;
    this.subscriptions.add(
      this.fileService.getFilesByContainer('CUSTOMS_CALCULATION_INVOICE', requestId).subscribe({
        next: (files) => {
          this.invoiceFile = files && files.length > 0 ? files[0] : null;
          this.loadingInvoice = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('ViewRequest: Error loading invoice', err);
          this.invoiceFile = null;
          this.loadingInvoice = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  getInvoiceDownloadUrl(): string | null {
    if (!this.invoiceFile) {
      return null;
    }
    return this.fileService.getDownloadUrl(this.invoiceFile.uid, this.invoiceFile.fileName);
  }

  downloadInvoice(event?: Event): void {
    event?.preventDefault();
    if (!this.invoiceFile) return;
    this.fileService.downloadFile(this.invoiceFile.uid, this.invoiceFile.fileName);
  }

  canDeleteRequest(): boolean {
    // Можно удалять только если нет сделки
    return this.request ? !this.request.hasDeal : false;
  }

  async deleteRequest(): Promise<void> {
    if (!this.requestId || !this.canDeleteRequest()) return;

    if (!await this.confirmDialog.confirm(this.deleteRequestConfirmText)) {
      return;
    }

    this.deletingRequest = true;
    this.subscriptions.add(
      this.cargoRequestService.deleteCargoRequest(this.requestId).subscribe({
        next: () => {
          this.deletingRequest = false;
          // После удаления возвращаемся на дашборд
          this.router.navigate(['/cargo-owner/dashboard/requests']);
        },
        error: (err) => {
          console.error('Error deleting request', err);
          this.deletingRequest = false;
          void this.confirmDialog.alert(err.error?.message || 'Ошибка при удалении запроса');
          this.cdr.markForCheck();
        }
      })
    );
  }

  scrollToChat(): void {
    document.getElementById('chat')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  editRequest(): void {
    if (this.requestId) {
      this.router.navigate(['/cargo-owner/edit-request', this.requestId]);
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  hasCalculationResults(): boolean {
    if (!this.request?.customsProducts || this.request.customsProducts.length === 0) {
      return false;
    }
    // Проверяем, есть ли результаты от AI или брокера
    return this.request.customsCalculationCompletedWithAi === true ||
           this.request.customsCalculationCompleted === true ||
           this.request.customsProducts.some(product => product.nameForBroker != null);
  }

  getTotalCustomsPayments(): number {
    if (!this.request?.customsProducts || this.request.customsProducts.length === 0) {
      return 0;
    }
    return this.request.customsProducts.reduce((total, product) => {
      return total + (product.totalCustomsPayments || 0);
    }, 0);
  }

  editCustomsProducts(): void {
    if (this.requestId) {
      // Переходим на страницу редактирования запроса, где можно редактировать товары таможенных платежей
      this.router.navigate(['/cargo-owner/edit-request', this.requestId]);
    }
  }

  /** Администратор назначил (или снял) менеджера запроса. */
  onManagerAssigned(change: AssigneeChange): void {
    if (this.request) {
      this.request = { ...this.request, assignedManagerId: change.id ?? undefined, assignedManagerName: change.name ?? undefined };
    }
  }
}
